import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Dialog, DialogContent, LinearProgress, TextField,
} from '@mui/material';
import Select from 'react-select';
import styled from 'styled-components';

import { Styling } from '../../constants';
import { IAppState } from '../../state/reducers';
import { IOrg, IUser } from '../../api/idm';
import { loadOrgs, loadOrgUsers, clearOrgUsers } from '../../state/user/impersonation';
import { beginImpersonation } from '../../utils/impersonation';
import toast, { superAdminToast } from '../../utils/toast';
import { VaultButton } from '../Shared';
import {
  DialogHeader,
  DialogHeaderTitle,
  ActionContainer,
} from '../Shared/styles/dialog';
import { IProps, IStateProps, IDispatchProps } from './interfaces';

type Option<T> = { label: string; value: T };

// ActionContainer is shared with other dialogs, so the spacing is added here rather
// than widened for everyone.
const FooterActions = styled(ActionContainer)`
  padding-bottom: ${Styling.DEFAULT_SPACING}px;
`;

/**
 * Menus render into the body instead of inline. Inside DialogContent they are otherwise
 * clipped by its scroll box, and the part that does fit paints under the fields below
 * it - which is why the Reason input shows through the open user list.
 */
const menuPortalProps = {
  menuPortalTarget: document.body,
  // MUI's Dialog sits at 1300; a portalled menu has to clear it.
  styles: { menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 1400 }) },
};

/**
 * The role is part of the label rather than a separate column so react-select's own filter and
 * sort keep working on it - typing "coach" narrows the list, which is the useful thing when an
 * org has more users than fit on screen. It is appended, not prefixed, so the list still sorts
 * by name.
 *
 * Roles load after the users (see loadOrgUsers), so an option can legitimately render without
 * one for a moment. Showing the name alone beats holding the whole list back.
 */
const toUserOption = (user: IUser, role?: string): Option<IUser> => ({
  label: `${user.displayName || user.userName} (${user.email})${role ? ` — ${role}` : ''}`,
  value: user,
});

const ImpersonateUserDialog: React.FC<IProps> = ({
  isOpen,
  onClose,
  orgLoadState,
  userLoadState,
  onLoadOrgs,
  onLoadOrgUsers,
  onClearOrgUsers,
}) => {
  // Nullable rather than bare Option: both start empty and the org's onChange clears the
  // user back to null, so the union is what the state actually holds.
  const [org, setOrg] = useState<Option<IOrg> | null>(null);
  const [user, setUser] = useState<Option<IUser> | null>(null);
  const [reason, setReason] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (isOpen && !orgLoadState?.orgs) onLoadOrgs();
  }, [isOpen]);

  const orgOptions: Array<Option<IOrg>> = (orgLoadState?.orgs ?? [])
    .map((o) => ({ label: o.displayName || o.name, value: o }))
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

  const userOptions: Array<Option<IUser>> = (userLoadState?.users ?? [])
    .map((u) => toUserOption(u, userLoadState?.roles?.[u.id]))
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));

  // A user can be picked before the roles arrive, and the option held in state was built
  // without one. Re-reading it from the current options means the closed control gains the
  // role too, instead of being the one place it never appears.
  const selectedUser = userOptions.find((o) => o.value.id === user?.value?.id) ?? user;

  const onStart = async () => {
    setStarting(true);
    try {
      // Reloads on success, so nothing after this runs in the happy path.
      // Blank means absent rather than an empty string in the audit record.
      await beginImpersonation(user.value.id, reason.trim() || undefined);
    } catch (e) {
      const error = e as any;
      const message = error?.response?.data?.message ?? error?.message ?? 'Unknown error';
      superAdminToast(false, `Could not start impersonation: ${message}`, { type: toast.TYPE.ERROR });
      setStarting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={starting ? null : onClose} fullWidth maxWidth="sm">
      <DialogHeader>
        <DialogHeaderTitle data-auto="ImpersonateUserTitle">Impersonate User</DialogHeaderTitle>
      </DialogHeader>
      {(orgLoadState?.loading || userLoadState?.loading || starting) && <LinearProgress />}
      <DialogContent>
        <div style={{ marginBottom: 16 }}>
          <Select
            {...menuPortalProps}
            placeholder="Organisation"
            value={org}
            options={orgOptions}
            onChange={(option: unknown) => {
              const selected = option as Option<IOrg>;
              setOrg(selected);
              setUser(null);
              onClearOrgUsers();
              if (selected) onLoadOrgUsers(selected.value);
            }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <Select
            {...menuPortalProps}
            placeholder="User"
            value={selectedUser}
            options={userOptions}
            isDisabled={!org || !!userLoadState?.loading}
            onChange={(option: unknown) => setUser(option as Option<IUser>)}
          />
        </div>
        <TextField
          label="Reason (optional)"
          helperText="Recorded in the impersonation audit log"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
        />
      </DialogContent>
      <FooterActions>
        <VaultButton variant="plainOnPaper" onClick={onClose} disabled={starting}>Cancel</VaultButton>
        <VaultButton
          variant="primaryOnWhite"
          onClick={onStart}
          disabled={!user || starting}
          data-auto="StartImpersonationBtn"
        >
          Impersonate
        </VaultButton>
      </FooterActions>
    </Dialog>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  orgLoadState: state.user?.impersonation?.orgLoadState,
  userLoadState: state.user?.impersonation?.userLoadState,
});

const mapDispatchToProps: IDispatchProps = {
  onLoadOrgs: loadOrgs,
  onLoadOrgUsers: loadOrgUsers,
  onClearOrgUsers: clearOrgUsers,
};

export default connect(mapStateToProps, mapDispatchToProps)(ImpersonateUserDialog);
